import React, { useState } from 'react';
import { FileNode } from '../../services/applications/types';

interface FileExplorerProps {
  files: FileNode[];
  selectedFile: string | null;
  onFileSelect: (file: FileNode) => void;
  onFileCreate: (parentPath: string, type: 'file' | 'folder') => void;
  onFileDelete: (filePath: string) => void;
  onFileRename: (filePath: string, newName: string) => void;
}

const FileExplorer: React.FC<FileExplorerProps> = ({
  files,
  selectedFile,
  onFileSelect,
  onFileCreate,
  onFileDelete,
  onFileRename
}) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    file: FileNode;
  } | null>(null);
  const [renamingFile, setRenamingFile] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState('');

  const toggleFolder = (path: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const handleContextMenu = (e: React.MouseEvent, file: FileNode) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      file
    });
  };

  const handleRename = (file: FileNode) => {
    setRenamingFile(file.path);
    setNewFileName(file.name);
    setContextMenu(null);
  };

  const confirmRename = () => {
    if (renamingFile && newFileName) {
      onFileRename(renamingFile, newFileName);
      setRenamingFile(null);
      setNewFileName('');
    }
  };

  const getFileIcon = (file: FileNode) => {
    if (file.type === 'folder') {
      return expandedFolders.has(file.path) ? 'fa-folder-open' : 'fa-folder';
    }
    
    // File icons based on extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'py':
        return 'fa-python text-blue-500';
      case 'json':
        return 'fa-file-code text-yellow-500';
      case 'txt':
        return 'fa-file-alt text-gray-500';
      case 'md':
        return 'fa-file-alt text-gray-500';
      case 'yml':
      case 'yaml':
        return 'fa-file-code text-red-500';
      default:
        return 'fa-file text-gray-400';
    }
  };

  const renderFile = (file: FileNode, level: number = 0) => {
    const isSelected = selectedFile === file.path;
    const isFolder = file.type === 'folder';
    const isExpanded = expandedFolders.has(file.path);
    const isRenaming = renamingFile === file.path;

    return (
      <div key={file.id}>
        <div
          className={`
            flex items-center px-2 py-1 cursor-pointer hover:bg-gray-700
            ${isSelected ? 'bg-blue-600 text-white' : 'text-gray-300'}
            ${level > 0 ? `ml-${level * 4}` : ''}
          `}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => {
            if (isFolder) {
              toggleFolder(file.path);
            } else {
              onFileSelect(file);
            }
          }}
          onContextMenu={(e) => handleContextMenu(e, file)}
        >
          <i className={`fas ${getFileIcon(file)} mr-2 text-sm`}></i>
          {isRenaming ? (
            <input
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onBlur={confirmRename}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  confirmRename();
                }
              }}
              className="bg-gray-800 text-white px-1 text-sm outline-none border border-blue-500"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="text-sm">{file.name}</span>
          )}
        </div>
        {isFolder && isExpanded && file.children && (
          <div>
            {file.children.map(child => renderFile(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full bg-gray-900 text-gray-300 relative">
      <div className="bg-gray-800 px-3 py-2 border-b border-gray-700 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase text-gray-400">Explorer</span>
        <div className="flex space-x-2">
          <button
            onClick={() => onFileCreate('/', 'file')}
            className="text-gray-400 hover:text-white text-xs"
            title="New File"
          >
            <i className="fas fa-file-plus"></i>
          </button>
          <button
            onClick={() => onFileCreate('/', 'folder')}
            className="text-gray-400 hover:text-white text-xs"
            title="New Folder"
          >
            <i className="fas fa-folder-plus"></i>
          </button>
        </div>
      </div>
      
      <div className="overflow-y-auto" style={{ height: 'calc(100% - 40px)' }}>
        {files.map(file => renderFile(file))}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setContextMenu(null)}
          />
          <div
            className="absolute bg-gray-800 border border-gray-600 rounded shadow-lg z-20 py-1"
            style={{ top: contextMenu.y - 50, left: contextMenu.x }}
          >
            <button
              className="block w-full text-left px-4 py-1 text-sm hover:bg-gray-700 text-gray-300"
              onClick={() => {
                onFileCreate(contextMenu.file.path, 'file');
                setContextMenu(null);
              }}
            >
              <i className="fas fa-file-plus mr-2"></i>
              New File
            </button>
            {contextMenu.file.type === 'folder' && (
              <button
                className="block w-full text-left px-4 py-1 text-sm hover:bg-gray-700 text-gray-300"
                onClick={() => {
                  onFileCreate(contextMenu.file.path, 'folder');
                  setContextMenu(null);
                }}
              >
                <i className="fas fa-folder-plus mr-2"></i>
                New Folder
              </button>
            )}
            <div className="border-t border-gray-700 my-1"></div>
            <button
              className="block w-full text-left px-4 py-1 text-sm hover:bg-gray-700 text-gray-300"
              onClick={() => handleRename(contextMenu.file)}
            >
              <i className="fas fa-edit mr-2"></i>
              Rename
            </button>
            <button
              className="block w-full text-left px-4 py-1 text-sm hover:bg-gray-700 text-red-400"
              onClick={() => {
                onFileDelete(contextMenu.file.path);
                setContextMenu(null);
              }}
            >
              <i className="fas fa-trash mr-2"></i>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FileExplorer;